import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { AppShell } from "@/components/shell/AppShell";
import { ModuleHeader, FeedChips, Toolbar, DateRangePills, Segmented, SectionTabs, EmptyState, Loading } from "@/components/kit";
import { useTenant } from "@/context/TenantContext";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { filterDays, kpisOf, presetsFor, ACCENT } from "./adhUtils";
import AdhOverview from "./AdhOverview";
import AdhTlAgents from "./AdhTlAgents";
import AdhAgentReport from "./AdhAgentReport";

const VIEWS = [
  { id: "overview", label: "Overview" },
  { id: "tl", label: "TL & Agents" },
];
const SITES = ["All", "Qatar", "India"];

export default function AdherencePage() {
  const { tenantId, tenant } = useTenant() || {};
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(params.get("view") || "overview");
  const [site, setSite] = useState(params.get("site") || "All");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [drawer, setDrawer] = useState(params.get("agent") || null);

  const load = useCallback((refresh = false) => {
    if (!tenantId) { setError("No client selected. Use the client switcher in the header."); return; }
    setBusy(true); setError(null);
    api.get(`/modules/adherence/dashboard?tenant_id=${tenantId}&refresh=${refresh}`)
      .then((r) => {
        setData(r.data);
        const p = presetsFor(r.data);
        setFrom((f) => f || `${p.month}-01`);
        setTo((t) => t || p.latest);
      })
      .catch((e) => setError(apiError(e)))
      .finally(() => setBusy(false));
  }, [tenantId]);
  useEffect(() => { setData(null); load(); }, [load]);

  useEffect(() => {
    const next = {};
    if (view !== "overview") next.view = view;
    if (site !== "All") next.site = site;
    if (drawer) next.agent = drawer;
    setParams(next, { replace: true });
  }, [view, site, drawer, setParams]);

  const presets = useMemo(() => (data ? presetsFor(data) : null), [data]);
  const slice = useMemo(() => (data && from && to ? filterDays(data, { from, to, site }) : null), [data, from, to, site]);
  const kpis = useMemo(() => (slice && data ? kpisOf(slice, data.targets) : null), [slice, data]);
  const isAdmin = ["admin", "super_admin"].includes(user?.role);

  return (
    <AppShell>
      <ModuleHeader testid="adh-title" title="Adherence" subtitle="Attendance & Discipline" accent={ACCENT}
        context={`${tenant?.name || "No client"} · Workspace data · monthly uploads`}
        meta={data ? `Snapshot · ${(data.months_loaded || []).length} months loaded` : null} onRefresh={data ? () => load(true) : null} busy={busy} />

      {error ? (
        <EmptyState testid="adh-error" title="No data for this module" body={error} isAdmin={isAdmin} action={{ to: "/settings?section=uploads", label: "Upload monthly data" }} />
      ) : !data || !slice ? <Loading /> : (
        <>
          <FeedChips testid="adh-coverage-banner" coverage={data.coverage} months={data.months_loaded || []}
            hint={`Bar ${data.targets.hours}h · grace ${data.targets.grace}m · durations exclude corrupt punches`} />
          <Toolbar testid="adh-toolbar">
            <DateRangePills prefix="adh" from={from} to={to} setFrom={setFrom} setTo={setTo} presets={presets.list} />
            <Segmented testidPrefix="adh-site" options={SITES} value={site} onChange={setSite} className="ml-auto" />
          </Toolbar>
          <SectionTabs testidPrefix="adh-view" tabs={VIEWS} value={view} onChange={setView} accent={ACCENT} />

          {view === "overview" && <AdhOverview data={data} slice={slice} kpis={kpis} />}
          {view === "tl" && <AdhTlAgents data={data} slice={slice} onOpenAgent={setDrawer} />}
          {drawer && <AdhAgentReport data={data} code={drawer} range={{ from, to }} onClose={() => setDrawer(null)} />}
        </>
      )}
    </AppShell>
  );
}
